import {
  Button,
  Card,
  EmptyState,
  Group,
  Icon,
  Input,
  Modal,
  Skeleton,
  Text,
  Title,
} from "@assetwatch/ui";
import type { ReactNode } from "react";
import { useState } from "react";
import { useSettingsCategoryMutations } from "../../mutations/useSettingsCategoryMutations";
import { useSettingsCategories } from "../../queries/settingsCategories";
import { useWorkScope } from "../WorkScopeProvider";
import { CategoryRow } from "./CategoryRow";

const INTRO =
  "One shared list, used everywhere a category applies — Work Orders, Requests, Inspections, Assets, Meters, Documents, and Procedures.";
const SKELETON_KEYS = ["first", "second", "third", "fourth"];

// Matches the POC: a 640px list card with a header bar, intro copy and rows.
function CategoriesCard({ children }: { children: ReactNode }) {
  return (
    <div className="max-w-160">
      <Card padding="none">
        {/* The DS Card lays children out with items-start, so the header and
            body need an explicit full width to span the card. */}
        <div className="flex w-full items-center justify-between gap-(--spacing-12) border-b border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-12)">
          <Title size="sm">Categories</Title>
        </div>
        <div className="flex w-full flex-col gap-(--spacing-8) px-(--spacing-20) pt-(--spacing-16)">
          <Text tone="subtle">{INTRO}</Text>
        </div>
        <div className="flex w-full flex-col pt-(--spacing-8)">{children}</div>
      </Card>
    </div>
  );
}

function AddCategoryRow({
  canManage,
  isPending,
  name,
  onChange,
  onAdd,
}: {
  canManage: boolean;
  isPending: boolean;
  name: string;
  onChange: (value: string) => void;
  onAdd: () => void;
}) {
  return (
    <div className="flex w-full items-center gap-(--spacing-8) p-(--spacing-12)">
      <div className="flex-1">
        <Input
          aria-label="New category"
          maxLength={100}
          onChange={(event) => onChange(event.target.value)}
          placeholder="New category"
          size="sm"
          reserveSubtextSpace={false}
          value={name}
          disabled={!canManage || isPending}
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        type="button"
        disabled={!canManage || isPending || !name.trim()}
        loading={isPending}
        onClick={onAdd}
      >
        <Icon name="plus-regular" size="sm" />
        Add
      </Button>
    </div>
  );
}

function CategoriesSkeleton() {
  return (
    <CategoriesCard>
      <output
        aria-label="Loading categories"
        className="flex flex-col gap-(--spacing-8) px-(--spacing-16) pb-(--spacing-16)"
      >
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} height="var(--spacing-40)" width="100%" />
        ))}
      </output>
    </CategoriesCard>
  );
}

export function CategoriesSettingsSection() {
  const { isError, isPending, selectedCustomer, selectedFacility } =
    useWorkScope();
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryToDelete, setCategoryToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const categories = useSettingsCategories(
    selectedCustomer?.id ?? "",
    selectedFacility?.id ?? "",
    !!selectedCustomer && !!selectedFacility,
  );
  const categoryMutations = useSettingsCategoryMutations();
  const canManageCategories = selectedFacility?.role === "ADMINISTRATOR";

  async function addCategory() {
    try {
      await categoryMutations.create.mutateAsync(newCategoryName.trim());
      setNewCategoryName("");
    } catch {
      // The mutation displays the error; keep the entered name for correction.
    }
  }

  async function renameCategory(id: number, name: string) {
    await categoryMutations.update.mutateAsync({ categoryId: id, name });
  }

  async function deleteCategory() {
    if (!categoryToDelete) return;
    try {
      await categoryMutations.remove.mutateAsync(categoryToDelete.id);
      setCategoryToDelete(null);
    } catch {
      // The mutation displays the error and leaves the confirmation open.
    }
  }

  if (isPending) return <CategoriesSkeleton />;
  if (isError)
    return (
      <div role="alert">
        <Text tone="subtle">Categories could not be loaded.</Text>
      </div>
    );
  if (!selectedFacility) return <Text tone="subtle">No site is selected.</Text>;
  if (categories.isPending) return <CategoriesSkeleton />;
  if (categories.isError) {
    return (
      <CategoriesCard>
        <div className="flex w-full flex-col items-start gap-(--spacing-12) px-(--spacing-16) pb-(--spacing-16)">
          <div role="alert">
            <Text tone="subtle">Categories could not be loaded.</Text>
          </div>
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => categories.refetch()}
          >
            Retry
          </Button>
        </div>
      </CategoriesCard>
    );
  }
  const rows = categories.data ?? [];
  return (
    <CategoriesCard>
      {rows.length === 0 ? (
        <div className="w-full px-(--spacing-16) pb-(--spacing-8)">
          <EmptyState
            variant="compact"
            icon="tag"
            title="No categories yet"
            headingLevel={3}
          />
        </div>
      ) : (
        <ul className="w-full">
          {rows.map((category) => (
            <CategoryRow
              key={category.id}
              id={category.id}
              name={category.name}
              isGlobal={category.isGlobal}
              canManage={canManageCategories}
              isSaving={categoryMutations.update.isPending}
              isDeleting={categoryMutations.remove.isPending}
              onSave={renameCategory}
              onDelete={(id, name) => setCategoryToDelete({ id, name })}
            />
          ))}
        </ul>
      )}
      <AddCategoryRow
        canManage={canManageCategories}
        isPending={categoryMutations.create.isPending}
        name={newCategoryName}
        onChange={setNewCategoryName}
        onAdd={() => void addCategory()}
      />
      <Modal
        open={categoryToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !categoryMutations.remove.isPending)
            setCategoryToDelete(null);
        }}
        title="Delete category?"
        description={`“${categoryToDelete?.name ?? ""}” will be removed from this list. Existing records will keep their category link.`}
        size="md"
      >
        <Group justify="end" gap={8} wrap="wrap">
          <Button
            onClick={() => setCategoryToDelete(null)}
            variant="ghost"
            disabled={categoryMutations.remove.isPending}
          >
            Cancel
          </Button>
          <Button
            intent="destructive"
            onClick={() => void deleteCategory()}
            loading={categoryMutations.remove.isPending}
          >
            Delete category
          </Button>
        </Group>
      </Modal>
    </CategoriesCard>
  );
}
